import { MeiliSearch, Config } from 'meilisearch';
import { generateTenantToken } from 'meilisearch/token';
import axios, { AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import { Headers as FetchHeaders } from 'cross-fetch';

export interface MeiliSearchConfig extends Omit<Config, 'httpClient'> {
  timeout?: number;
  retries?: number;
}

class MeiliSearchClient extends MeiliSearch {
  private static normalizeHeaders(headers: HeadersInit | undefined): Record<string, string> | undefined {
    if (!headers) {
      return undefined;
    }

    const HeadersCtor: typeof Headers =
      typeof Headers !== 'undefined' ? Headers : (FetchHeaders as unknown as typeof Headers);
    const headersInstance = headers instanceof HeadersCtor ? headers : new HeadersCtor(headers);
    const normalizedHeaders: Record<string, string> = {};

    headersInstance.forEach((value, key) => {
      normalizedHeaders[key.toLowerCase()] = value;
    });

    return normalizedHeaders;
  }

  private static createAxiosInstance(config: MeiliSearchConfig): AxiosInstance {
    const axiosInstance = axios.create({
      timeout: config.timeout ?? 5000
    });

    axiosRetry(axiosInstance, {
      retries: config.retries ?? 3,
      retryDelay: () => 0,
      retryCondition: (error) => {
        const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
        const isNetworkError = axiosRetry.isNetworkOrIdempotentRequestError(error);
        const is400Error = error.response?.status === 400;
        // Retry 400s due to transient auth connectivity issues in our MeiliSearch infrastructure
        // where the node doesn't have proper auth connectivity initially but establishes it on retry
        const is5xxError = (error.response?.status ?? 0) >= 500 && (error.response?.status ?? 0) < 600;
        const shouldRetry = isTimeout || isNetworkError || is400Error || is5xxError;
        console.log('[MeiliSearchClient] Retry condition check:', {
          errorMessage: error.message,
          status: error.response?.status,
          isTimeout,
          isNetworkError,
          is400Error,
          is5xxError,
          willRetry: shouldRetry
        });
        return shouldRetry;
      },
      onRetry: (retryCount, error, requestConfig) => {
        const message =
          typeof error === 'object' && error !== null && 'message' in error ? (error as any).message : String(error);
        console.warn(`[MeiliSearchClient] Retry attempt #${retryCount} for ${requestConfig?.url}: ${message}`, {
          body: requestConfig?.data
        });
      },
      shouldResetTimeout: true
    });

    return axiosInstance;
  }

  constructor(config: MeiliSearchConfig) {
    const axiosInstance = MeiliSearchClient.createAxiosInstance(config);

    super({
      ...config,
      httpClient: async (url, opts) => {
        const method = (opts?.method ?? 'GET').toUpperCase();
        // Log when the request is triggered
        console.log(`[MeiliSearchClient] ${method} request to ${url} triggered`, {
          body: opts?.body
        });
        try {
          const response = await axiosInstance.request({
            url: url.toString(),
            method,
            headers: opts?.headers ? MeiliSearchClient.normalizeHeaders(opts.headers) : undefined,
            data: opts?.body
          });
          // Log after request is done
          console.log(`[MeiliSearchClient] ${method} request to ${url} completed with status ${response.status}`);
          return response.data;
        } catch (error) {
          // Log the error but don't rethrow it yet to allow retry mechanism to work
          const errorMessage = error instanceof Error ? error.message : String(error);
          console.error(`[MeiliSearchClient] Error in ${method} request to ${url}: ${errorMessage}`);
          // Rethrow the error to allow retry mechanism to work
          throw error;
        }
      }
    });
  }
}

export * from 'meilisearch';
export { MeiliSearchClient as MeiliSearch };
export { generateTenantToken };
